// public/js/features/settings/settings-derived-sensors.js
// Settings → Derived sensors tab (tenant admin / superadmin).
//   Turn a raw sensor reading into a digital 0/1 state with a threshold — e.g.
//   distance < 50 cm = "loaded". Computed at ingest, stored like any sensor value,
//   so it is historical, chartable and alertable. See docs/derived-sensors.md.
//
// This edits the TENANT DEFAULT rules (tenant.sensorRules). A single asset can
// override a rule on its detail page (asset-derived-section.js).
import { getCurrentUser, can, isAdminLevel } from '../../modules/rbac.js';
import { loadTenants, updateTenant } from '../../modules/tenants.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { helpIcon, initHelp } from '../../ui/help.js';
import { mountRuleEditor } from './derived-rule-editor.js';

const HELP = {
  intro:
    '<b>Derived sensors</b><br>' +
    'Turn a raw reading into a plain on/off state with one threshold, so crews see a word ' +
    '("loaded") instead of a number, and you can alert on the state. The state is computed on ' +
    'every reading and stored in history like any other sensor value.' +
    '<br><br><b>Aviation:</b> an engine stand with a distance sensor reads "loaded" when the ' +
    'engine sits on it (distance under 50 cm) and "empty" when it is removed.',
};

let selectedTenantId = null;

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export async function renderDerivedSensorSettings(container) {
  const groups = getCurrentUser().groups || [];
  const isSuper = isAdminLevel(groups);
  const canTenant = can(groups, 'manageTenants');
  if (!isSuper && !canTenant) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  container.innerHTML = `<div class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>`;

  let tenants;
  try { tenants = await loadTenants(); }
  catch (e) { container.innerHTML = `<div class="alert alert-danger">Failed to load tenants.</div>`; return; }

  // Global tenant scope (hard-coupled): the sidebar selector is the ONLY
  // tenant choice — under "All tenants" this page just prompts for a scope.
  const scopeTid = scopeTenantParam();
  const scopePinned = scopeTid && scopeTid !== 'unassigned' && tenants.some(t => t.tenantId === scopeTid);
  if (tenants.length > 1 && !scopePinned) {
    container.innerHTML = scopePromptHtml('derived-sensor templates');
    return;
  }
  selectedTenantId = scopePinned ? scopeTid : (tenants[0]?.tenantId || null);
  const tenant = tenants.find(t => t.tenantId === selectedTenantId) || null;
  const reload = () => renderDerivedSensorSettings(container);
  const rules = Array.isArray(tenant?.sensorRules) ? tenant.sensorRules : [];

  container.innerHTML = `
    <div class="card shadow-sm mb-4 border-primary">
      <div class="card-header bg-primary text-white d-flex align-items-center gap-2">
        <i class="fa-solid fa-wave-square"></i>
        <span class="fw-semibold">Derived sensors for <span class="tenant-name">${esc(tenant?.name || selectedTenantId || '')}</span></span>
        <span class="badge bg-light text-primary ms-auto">applies to this tenant only</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">Threshold rule <b>templates</b> that turn a raw reading into an on/off state${helpIcon('intro')}. A <b>Default</b> template runs on every sensor that reads its metric; a plain template is applied per sensor. Pick which templates apply — and override or add custom rules per sensor — on each asset's detail page.</p>
        <div id="ds-editor"></div>
        <div class="d-flex mt-2">
          <button id="ds-save" class="btn btn-primary btn-sm ms-auto"><i class="fa-solid fa-check me-1"></i>Save rules</button>
        </div>
      </div>
    </div>`;

  initHelp(container, HELP);

  container.querySelector('#ds-tenant')?.addEventListener('change', (e) => {
    selectedTenantId = e.target.value;
    reload();
  });

  const editor = mountRuleEditor(container.querySelector('#ds-editor'), rules);

  container.querySelector('#ds-save')?.addEventListener('click', async () => {
    const btn = container.querySelector('#ds-save');
    const { rules: out, error } = editor.read();
    if (error) { window.showToast(error, 'warning'); return; }
    btn.disabled = true;
    try {
      await updateTenant(selectedTenantId, { sensorRules: out });
      window.showToast('Derived sensor rules saved', 'success');
      reload();
    } catch (e) {
      window.showToast('Failed to save', 'danger');
      btn.disabled = false;
    }
  });
}
